import { AssetCameraKey, AssetId, CameraId } from "@visioni/a3s-schema";

/**
 * The camera an activation's a3s marker names, as the `{asset}@{camera}` key the
 * app routes cameras by. Absent for an activation the marker did not place.
 */
export function activationCameraKey(activation: {
  readonly a3sAsset?: string | undefined;
  readonly a3sCamera?: string | undefined;
}): AssetCameraKey | undefined {
  if (activation.a3sAsset === undefined || activation.a3sCamera === undefined) return undefined;
  try {
    return AssetCameraKey.fromParts(AssetId.fromString(activation.a3sAsset), CameraId.fromString(activation.a3sCamera));
  } catch {
    return undefined;
  }
}

const pad = (n: number): string => String(n).padStart(2, "0");

/**
 * How long one step of an activation's handling took, e.g. `2m 05s` from created
 * to responded. Two units at most — the precision an operator reasons in.
 */
export function formatElapsed(fromIso: string, toIso: string): string | undefined {
  const ms = Date.parse(toIso) - Date.parse(fromIso);
  if (!Number.isFinite(ms) || ms < 0) return undefined;

  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${String(days)}d ${String(hours)}h`;
  if (hours > 0) return `${String(hours)}h ${pad(minutes)}m`;
  if (minutes > 0) return `${String(minutes)}m ${pad(seconds)}s`;
  return `${String(seconds)}s`;
}

/**
 * Oldest first — the order an activation's story is read in. The replica makes
 * no promise about the order an activation's signals and operator notes come back.
 */
export function chronological<T>(rows: readonly T[], instantOf: (row: T) => string): readonly T[] {
  return [...rows].sort((a, b) => Date.parse(instantOf(a)) - Date.parse(instantOf(b)));
}
